/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useEffect } from "react";
import * as React from "react";
import styled from "styled-components";
import { devices } from "components/shared/Styles";
import { AppPath } from "components/AppPath";
import { useCurrentUser } from "actions/CurrentUserActions";
import { CmsApi, GuidePage as GuidePageType } from "api/CmsApi";
import { useMounted } from "hooks/useMounted";
import { getUserLocale } from "utilities/UserSettingsUtilities";
import { LocaleLink } from "utilities/NavigationUtilities";
import { FooterLogoModal } from "components/shared/FooterLogoModal";
import { isRemoveMusicUsecasePageEnabled } from "PhonosConstants";

const Footer = styled.footer`
  display: flex;
  flex-direction: row;
  gap: 64px;
  width: 100%;
  background-color: var(--spectrum-global-color-static-gray-200);

  ${devices.small} {
    gap: 32px;
  }
`;

const FooterContainer = styled.nav`
  width: calc(100% - 208px);
  max-width: 1156px;
  margin: auto;
  padding: 54px;

  ${devices.small} {
    width: 100%;
    margin: 0;
    padding: 32px;
  }
`;

const FooterColumns = styled.div`
  display: flex;
  flex-direction: row;
  gap: 64px;
  width: 100%;
  align-items: flex-start;

  ${devices.small} {
    flex-direction: column;
    gap: 32px;
  }
`;

const FooterColumn = styled.ul`
  display: flex;
  flex-direction: column;
  justify-content: left;
  gap: 12px;
  width: 250px;
  font-size: 16px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  color: var(--spectrum-global-color-static-black);
  order: 1;

  a {
    text-decoration: none;
    color: var(--spectrum-global-color-static-black);
  }

  ${devices.small} {
    width: 100%;
  }
`;

const LegalColumn = styled(FooterColumn)`
  font-size: 14px;
  gap: 8px;
  line-height: 16px;
  color: var(--spectrum-global-color-static-gray-700);

  a {
    color: var(--spectrum-global-color-static-gray-700);
  }

  ${devices.small} {
    order: 2;
  }
`;

const FooterColumnTitle = styled.h2`
  font-weight: 800;
  line-height: 16px;

  img {
    width: 120px;
    height: 40px;
    margin-bottom: 8px;
  }
`;

export const AppFooter: React.FC = () => {
  const hasPremiumAccess =
    useCurrentUser().currentUser?.hasPremiumAccess ?? false;

  const mounted = useMounted();
  const [currentGuidePages, setGuidePages] = React.useState<GuidePageType[]>(
    []
  );
  useEffect(() => {
    CmsApi.instance
      .getAllGuidePages({ locale: getUserLocale()!, count: 5 })
      .then((guidePages) => {
        if (!mounted.current) {
          return;
        }

        setGuidePages(guidePages);
      })
      .catch((error) => {
        newrelic.noticeError(error);
      });
  }, [setGuidePages, mounted]);

  return (
    <Footer>
      <FooterContainer>
        <FooterColumns>
          <LegalColumn>
            <li key="logo">
              <FooterLogoModal />
            </li>
            <li key="privacy">
              <a href="https://www.adobe.com/privacy.html">
                {I18n.t("home.footer.privacy")}
              </a>
            </li>
            <li key="terms">
              <a href="https://www.adobe.com/legal/terms.html">
                {I18n.t("home.footer.terms")}
              </a>
            </li>
            <li key="cookies">
              <a href="#" id="feds_cookie_preferences_link">
                {I18n.t("home.footer.cookie_preferences")}
              </a>
            </li>
            <li key="rights">
              <a href="https://www.adobe.com/privacy/us-rights.html">
                {I18n.t("home.footer.do_not_share")}
              </a>
            </li>
            <div>
              <span
                dangerouslySetInnerHTML={{
                  __html: I18n.t("home.footer.copyright", {
                    currentYear: new Date().getFullYear(),
                  }) as string,
                }}
              />
            </div>
          </LegalColumn>
          <FooterColumn>
            <FooterColumnTitle>
              {I18n.t("home.footer.use_cases.title")}
            </FooterColumnTitle>
            <li key="transcription">
              <LocaleLink to={AppPath.TRANSCRIPTION_PAGE}>
                {I18n.t("use_cases.transcription_page.title")}
              </LocaleLink>
            </li>
            <li key="record">
              <LocaleLink to={AppPath.RECORD_PAGE}>
                {I18n.t("use_cases.record_page.title")}
              </LocaleLink>
            </li>
            <li key="edit">
              <LocaleLink to={AppPath.EDIT_WITH_PODCAST_PAGE}>
                {I18n.t("use_cases.edit_audio_page.title")}
              </LocaleLink>
            </li>
            <li key="audiogram">
              <LocaleLink to={AppPath.CONVERT_AUDIO_TO_VIDEO_PAGE}>
                {I18n.t("use_cases.convert_audio_to_video_page.title")}
              </LocaleLink>
            </li>
            <li key="captions">
              <LocaleLink to={AppPath.ADD_CAPTIONS_TO_VIDEO_PAGE}>
                {I18n.t("use_cases.add_captions_to_video_page.title")}
              </LocaleLink>
            </li>
            {isRemoveMusicUsecasePageEnabled && (
              <li key="remove-music">
                <LocaleLink to={AppPath.REMOVE_MUSIC_PAGE}>
                  {I18n.t("use_cases.remove_music_page.title")}
                </LocaleLink>
              </li>
            )}
          </FooterColumn>
          {currentGuidePages.length > 0 && (
            <FooterColumn>
              <FooterColumnTitle>
                <LocaleLink to={AppPath.GUIDES}>
                  {I18n.t("guides.title")}
                </LocaleLink>
              </FooterColumnTitle>
              {currentGuidePages.map((guidePage) => {
                return (
                  <li key={guidePage.id}>
                    <LocaleLink
                      key={guidePage.id}
                      to={AppPath.GUIDES}
                      pathParams={{
                        guidePageSlug: guidePage.attributes.slug,
                      }}
                    >
                      {guidePage.attributes.shortTitle}
                    </LocaleLink>
                  </li>
                );
              })}
            </FooterColumn>
          )}
          <FooterColumn>
            <FooterColumnTitle>
              {I18n.t("home.footer.resources.title")}
            </FooterColumnTitle>
            <li key="system-requirements">
              <a
                href={I18n.t(
                  "home.footer.resources.system_requirements_link_url"
                )}
              >
                {I18n.t("home.footer.resources.system_requirements_link")}
              </a>
            </li>
            <li key="faq">
              <a href={I18n.t("home.footer.resources.faq_link_url")}>
                {I18n.t("home.footer.resources.faq_link")}
              </a>
            </li>
            <li key="survey">
              <a
                href={
                  hasPremiumAccess
                    ? "https://survey.adobe.com/jfe/form/SV_5tf2KstbQekornE?pl=pr"
                    : "https://survey.adobe.com/jfe/form/SV_5tf2KstbQekornE?pl=fr"
                }
              >
                {I18n.t("home.footer.resources.feedback_link")}
              </a>
            </li>
            <li key="forum">
              <a href="https://community.adobe.com/t5/adobe-podcast/ct-p/ct-adobe-podcast">
                {I18n.t("home.footer.resources.forum_link")}
              </a>
            </li>
          </FooterColumn>
        </FooterColumns>
      </FooterContainer>
    </Footer>
  );
};
